import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Chip } from "@mui/material";
import { Chat, Campaign } from "@mui/icons-material";
import Message from "./Message";

const MessagesList = ({
  activeMessageId,
  onMessageClick,
  onConversationClick,
  conversationActive,
}) => {
  const messages = useSelector((state) => state.messages);

  // Separate global messages from direct messages
  const { globalMessages, directMessages } = useMemo(() => {
    const global = [];
    const direct = [];
    messages.forEach((msg) => {
      if (msg.sentTo === "global") {
        global.push(msg);
      } else {
        direct.push(msg);
      }
    });
    return { globalMessages: global, directMessages: direct };
  }, [messages]);

  const numberUnread = useMemo(() => {
    return messages.reduce((count, msg) => {
      // Only count unread messages from admin (not user's own messages)
      const isFromAdmin =
        msg.senderType === "admin" ||
        !msg.senderType ||
        msg.sentTo === "global";
      return count + (!msg.read && isFromAdmin ? 1 : 0);
    }, 0);
  }, [messages]);

  const directUnread = useMemo(() => {
    return directMessages.reduce((count, msg) => {
      // Only count unread messages from admin (not user's own messages)
      const isFromAdmin = msg.senderType === "admin" || !msg.senderType;
      return count + (!msg.read && isFromAdmin ? 1 : 0);
    }, 0);
  }, [directMessages]);

  return (
    <Box className="messages_list scroller">
      <Typography variant="h3">Inbox ({numberUnread})</Typography>

      {/* Direct Messages - Show as conversation thread */}
      {directMessages.length > 0 && (
        <Box
          className={`message ${conversationActive ? "message--active" : ""} ${directUnread === 0 ? "message--read" : ""}`}
          onClick={onConversationClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
          }}
        >
          {directUnread > 0 && <div className="message_unread-badge" />}
          <Chat sx={{ fontSize: 18, color: "#197b68" }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: "0 !important",
                fontWeight: directUnread > 0 ? "bold" : "normal",
              }}
            >
              Conversation with Mindstack
            </Typography>
          </Box>
          {directUnread > 0 && (
            <Chip
              label={directUnread}
              size="small"
              color="error"
              sx={{ height: 20, minWidth: 20 }}
            />
          )}
        </Box>
      )}

      {/* Global Messages */}
      {globalMessages.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 2,
              mb: 1,
              px: 1,
            }}
          >
            <Campaign sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: "0 !important" }}
            >
              Announcements
            </Typography>
          </Box>
          {globalMessages.map(({ messageId, subject, sentAt, read }) => (
            <Message
              key={messageId}
              active={activeMessageId === messageId}
              messageId={messageId}
              subject={subject}
              sentAt={sentAt}
              read={read}
              onMessageClick={onMessageClick}
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default MessagesList;
